import React from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Chip,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  Typography,
  alpha,
} from "@mui/material";
import { ArrowBack, EditOutlined } from "@mui/icons-material";
import { getInitials } from "../../utils/utils";

function statusChipProps(statusLabel) {
  const s = String(statusLabel || "").toLowerCase();
  if (s.includes("inativo")) return { label: "Inativo", color: "default", filled: false };
  return { label: "Ativo", color: "success", filled: true };
}

/**
 * Cabeçalho da edição/criação — visual alinhado ao ClientDetailHeader.
 */
export default function ClientFormPageChrome({
  mode = "edit",
  clientId,
  clientName,
  clientSubtitle,
  clientTypeLabel,
  clientStatusLabel,
  listPage = 1,
  onCancel,
}) {
  const navigate = useNavigate();
  const isEdit = mode === "edit" && clientId;
  const displayName = clientName?.trim() || (isEdit ? `Cliente #${clientId}` : "Novo cliente");
  const subtitle = String(clientSubtitle || "").trim();
  const detailsHref = isEdit ? `/clients/${clientId}` : `/clients?page=${listPage}`;
  const statusProps = statusChipProps(clientStatusLabel);

  const handleBack = () => {
    if (typeof onCancel === "function") {
      onCancel();
      return;
    }
    navigate(detailsHref);
  };

  return (
    <Paper
      variant="outlined"
      sx={{
        borderRadius: 2.5,
        overflow: "hidden",
        mb: 2,
        flexShrink: 0,
        boxShadow: (t) => `0 1px 3px ${alpha(t.palette.common.black, 0.06)}`,
      }}
    >
      <Box
        sx={{
          px: { xs: 2, md: 3 },
          pt: { xs: 2, md: 2.5 },
          pb: 2,
          background: (t) =>
            `linear-gradient(180deg, ${alpha(t.palette.primary.main, 0.05)} 0%, transparent 100%)`,
        }}
      >
        <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ mb: 1.75 }}>
          <Tooltip title={isEdit ? "Voltar ao perfil" : "Voltar à lista"}>
            <IconButton size="small" onClick={handleBack} sx={{ mt: 0.25, flexShrink: 0 }}>
              <ArrowBack fontSize="small" />
            </IconButton>
          </Tooltip>

          <Stack direction="row" spacing={2} alignItems="center" sx={{ flex: 1, minWidth: 0 }}>
            <Box
              sx={{
                width: { xs: 48, md: 56 },
                height: { xs: 48, md: 56 },
                borderRadius: "50%",
                bgcolor: "primary.main",
                color: "primary.contrastText",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontWeight: 700,
                fontSize: { xs: "1rem", md: "1.25rem" },
                boxShadow: 2,
                border: "2px solid",
                borderColor: "background.paper",
                flexShrink: 0,
              }}
            >
              {isEdit ? getInitials(displayName) : "+"}
            </Box>

            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography
                variant="h5"
                component="h1"
                sx={{ fontWeight: 800, lineHeight: 1.25, letterSpacing: "-0.02em", wordBreak: "break-word" }}
              >
                {isEdit ? displayName : "Novo cliente"}
              </Typography>
              {isEdit && subtitle ? (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.35, fontWeight: 500 }}>
                  {subtitle}
                </Typography>
              ) : null}
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, fontWeight: 500 }}>
                {isEdit ? "Editar cadastro" : "Cadastrar novo cliente"}
              </Typography>
            </Box>
          </Stack>
        </Stack>

        <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ pl: { xs: 0, sm: 6.5 } }}>
          {isEdit ? (
            <Chip size="small" label={`ID ${clientId}`} sx={{ bgcolor: "action.selected", fontWeight: 600 }} />
          ) : null}
          {clientTypeLabel ? <Chip size="small" variant="outlined" label={clientTypeLabel} /> : null}
          {isEdit && clientStatusLabel ? (
            <Chip
              size="small"
              label={statusProps.label}
              color={statusProps.color}
              variant={statusProps.filled ? "filled" : "outlined"}
            />
          ) : null}
          <Chip
            size="small"
            icon={<EditOutlined sx={{ fontSize: "0.9rem !important" }} />}
            label="Modo edição"
            variant="outlined"
            color="primary"
          />
        </Stack>
      </Box>
    </Paper>
  );
}
